import {useAui} from '@assistant-ui/react';
import type {ActionResult} from '@wandb/forge-shell-contract/aria';
import {useLayoutEffect, useRef} from 'react';

import {useViewer} from '../../state/viewer/hooks';
import {draftKey, useDraftStore} from '../chat/composer';
import {validateImageParts} from '../chat/composer/imageParts';
import type {WBAgentController} from '../chat/controller';
import {wbAgentControllerStore} from '../chat/controller';
import {
  isUserRepresentable,
  toUserMessageContent,
} from '../chat/messageConversions';
import type {AgentChatStore} from '../chat/store';
import {
  hasMeaningfulContent,
  selectActiveThread,
  useAgentChatStore,
} from '../chat/store';
import {createThread} from '../chat/store/actions';
import type {ContentPart} from '../chat/store/thread';
import {
  isAgentChatReadonly,
  viewerOwnsThread,
} from '../chat/useAgentChatReadonly';
import {closeCustomizationSection} from './customizations/useCustomizationsStore';
import {useTrackWBAgentComposer} from './useTrackWBAgent';
import {useWBAgentWindowStore} from './window/useWBAgentWindowStore';

type Aui = ReturnType<typeof useAui>;
type ComposerTrackers = Pick<
  ReturnType<typeof useTrackWBAgentComposer>,
  'trackEntryPointClicked' | 'trackMessageSent'
>;

/**
 * WBAgentControllerBridge registers the imperative controller handle while the
 * agent chat is mounted, so buttons anywhere in the app can open the chat or
 * send a message. It must render inside `AuiProvider` (it reads `useAui`) and
 * is mounted only when the feature is available; the handle is cleared on
 * unmount. Renders nothing.
 */
export const WBAgentControllerBridge = (): null => {
  const aui = useAui();
  const store = useAgentChatStore();
  const {trackEntryPointClicked, trackMessageSent} = useTrackWBAgentComposer();
  // NOTE: The ref keeps the tracker out of the effect deps, so the handle
  // re-registers only on `aui` changes.
  const trackersRef = useRef({trackEntryPointClicked, trackMessageSent});
  // oxlint-disable-next-line react/refs
  trackersRef.current = {trackEntryPointClicked, trackMessageSent};
  const viewerUsername = useViewer()?.username;

  // NOTE: Layout effect clears the handle before paint, beating any click that
  // would otherwise reach a handle whose runtime is being torn down.
  useLayoutEffect(
    () =>
      wbAgentControllerStore.registerHandle(
        createController(aui, store, () => trackersRef.current, viewerUsername)
      ),
    [aui, store, viewerUsername]
  );

  return null;
};

function createController(
  aui: Aui,
  store: AgentChatStore,
  getTrackers: () => ComposerTrackers,
  viewerUsername: string | undefined
): WBAgentController {
  const openChat = () => {
    useWBAgentWindowStore.getState().setOpen(true);
    closeCustomizationSection();
  };
  return {
    startChat(
      prompt,
      {attachments, delivery, entryPoint, placeholder, target}
    ) {
      if (entryPoint != null) {
        getTrackers().trackEntryPointClicked(entryPoint);
      }
      if (isAgentChatReadonly()) {
        return {ok: false, reason: 'readonly'};
      }

      const parts = messageParts(prompt, attachments);
      const normalizedPlaceholder = placeholder?.trim() || undefined;
      if (
        !hasMeaningfulContent(parts.filter(isUserRepresentable)) &&
        (delivery === 'send' || normalizedPlaceholder == null)
      ) {
        return {ok: false, reason: 'empty'};
      }

      // Another user's shared chat is read-only, so prompts go to a new chat.
      const thread = selectActiveThread(store.getState());
      const activeThread = viewerOwnsThread(thread, viewerUsername)
        ? thread
        : undefined;

      const incomingImages = parts.filter(part => part.type === 'image');
      const existingImages =
        delivery === 'draft' && target === 'active-or-new' && activeThread
          ? (useDraftStore.getState().drafts[draftKey(activeThread.id)]
              ?.files ?? [])
          : [];
      if (!validateImageParts([...existingImages, ...incomingImages]).ok) {
        return {ok: false, reason: 'attachment-limit'};
      }

      if (delivery === 'send') {
        if (target === 'new' || activeThread == null) {
          aui.threads().switchToNewThread();
        }
        aui.thread().append({
          role: 'user',
          content: toUserMessageContent(parts),
        });
        getTrackers().trackMessageSent('programmatic');
        openChat();
        return {ok: true};
      }

      if (target === 'active-or-new' && activeThread != null) {
        useDraftStore
          .getState()
          .appendDraftParts(
            draftKey(activeThread.id),
            parts,
            normalizedPlaceholder
          );
        openChat();
        return {ok: true};
      }

      const action = createThread();
      const committed = useDraftStore
        .getState()
        .setDraftParts(draftKey(action.id), parts, normalizedPlaceholder);
      if (committed) {
        store.dispatch(action);
      }
      openChat();
      return committed ? {ok: true} : {ok: false, reason: 'occupied'};
    },
    openChat,
    openChatToThread(threadId: string) {
      aui.threads().switchToThread(threadId);
      openChat();
    },
  };
}

// Normalize the two prompt shapes and prepend optional structured context.
function messageParts(
  prompt: string | ContentPart[],
  attachments: ContentPart[] = []
): ContentPart[] {
  if (typeof prompt !== 'string') {
    return [...attachments, ...prompt];
  }
  const text = prompt.trim();
  return text.length > 0
    ? [...attachments, {type: 'text', text, status: 'complete'}]
    : attachments;
}
